import { FunctionalComponent, h } from 'preact';
import { Day, noOp, Price } from '../../../../common';
import { PriceFormat } from '../../../../i18n/Format/PriceFormat';
import { withStyles } from '../../../../style';
import { isBlank } from '../../../../utils';
import { PriceFormatOptions } from '../../../../i18n';
import * as styles from './CalendarDay.scss';

const inBetween = ({ id }: Day, departureDate: string, returnDate: string): boolean => {
  if (isBlank(departureDate) || isBlank(returnDate)) {
    return false;
  }
  return departureDate < id && id < returnDate;
};

export interface CalendarDayProps {
  index: number;
  day: Day;
  showAvailability: boolean;
  isDepartureDate: boolean;
  isReturnDate: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  isAvailable: boolean;
  price: Price;
  options: PriceFormatOptions;
  hasAvailability: boolean;
  departureDate: string;
  returnDate: string;
  onSelect: (e: Event, date: string) => false;
  onHoverEnd: (id: string) => void;
  isHovered: boolean;
  isHoverEnd: boolean;
  showPrice: boolean;
  highlightCheapestPrice: boolean;
}

export const CalendarDay: FunctionalComponent<CalendarDayProps> = withStyles(styles)(
  ({
    tx,
    index,
    day,
    showAvailability,
    isDepartureDate,
    isReturnDate,
    isDisabled,
    isSelected,
    isAvailable,
    price,
    options,
    hasAvailability,
    departureDate,
    returnDate,
    onSelect,
    onHoverEnd,
    isHovered,
    isHoverEnd,
    showPrice,
    highlightCheapestPrice,
  }) => {
    const setHoverDay = () => {
      onHoverEnd(day.id);
    };

    const removeHoverDay = () => {
      onHoverEnd(undefined);
    };

    return (
      <div
        key={day.id}
        class={tx('calendarBlock', { departureDate: isDepartureDate, returnDate: isReturnDate })}
        style={{ msGridColumn: String((index % 7) + 1), msGridRow: String(Math.floor(index / 7) + 2) }}
      >
        <div
          class={tx('flex flex--column ps-xsmall-t ps-xxsmall-b', {
            'bw-calendar-day day': !day.fill,
            hovered: isHovered,
            hoverEnd: isHoverEnd,
            fill: day.fill,
            selectionStart: isDepartureDate,
            selectionPath: inBetween(day, departureDate, returnDate),
            selectionEnd: isReturnDate,
            'nordic-blue-900-text': !isSelected,
            'white-text nordic-blue-900-bg': isSelected,
            disabledDay: isDisabled,
            availableDay: isAvailable,
          })}
          onMouseEnter={setHoverDay}
          onMouseLeave={removeHoverDay}
          onClick={day.fill || isDisabled ? noOp : (e: Event): void => onSelect(e, day.id)}
        >
          {!day.fill && <div class={tx('flex--nogrow font-heading-5 medium-type')}>{day.value}</div>}
          {!day.fill && !isDisabled && !inBetween(day, departureDate, returnDate) && showPrice && (
            <div
              class={tx('price', 'flex--nogrow font-body-2 nowrap', {
                'medium-type white-text': isSelected,
                'nordic-blue-900-text': !isSelected && !highlightCheapestPrice,
                'bold-type heather-700-text': !isSelected && highlightCheapestPrice,
              })}
            >
              <PriceFormat price={price} options={options} />
            </div>
          )}
          {showAvailability && !day.fill && (
            <div
              class={tx('flex--nogrow availability', {
                available: hasAvailability,
                'not-available': !hasAvailability,
              })}
            />
          )}
        </div>
      </div>
    );
  }
);
